import React, { useState } from 'react';
import { filesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Search, FileText, Download, Loader2 } from 'lucide-react';
import { formatDateTime, formatFileSize, getStatusLabel } from '../../lib/utils';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);
    try {
      const response = await filesAPI.search(query, statusFilter === 'all' ? undefined : statusFilter);
      setResults(response.data);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Recherche</h1>
        <p className="text-slate-600">Recherchez des fichiers par nom ou statut.</p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Nom du fichier..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="search-status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="recu">Reçu</SelectItem>
                <SelectItem value="en_cours_revision">En cours de révision</SelectItem>
                <SelectItem value="approuve">Approuvé</SelectItem>
                <SelectItem value="refuse">Refusé</SelectItem>
                <SelectItem value="a_completer">À compléter</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={loading || !query.trim()} data-testid="search-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Rechercher
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {searched && (
        <Card>
          <CardContent className="p-0">
            {results.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Aucun résultat trouvé.</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-slate-100">
                  <p className="text-sm text-slate-600">{results.length} fichier(s) trouvé(s)</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fichier</TableHead>
                      <TableHead className="hidden md:table-cell">Projet</TableHead>
                      <TableHead className="hidden md:table-cell">Taille</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="hidden lg:table-cell">Date</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium">{file.nom_fichier_original}</p>
                              <p className="text-xs text-slate-500">{file.uploaded_by_name}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Link
                            to={`/portail/projets/${file.project_id}`}
                            className="text-primary hover:underline"
                          >
                            {file.project_nom}
                          </Link>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {formatFileSize(file.taille_octets)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`status-${file.statut}`} variant="secondary">
                            {getStatusLabel(file.statut)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {formatDateTime(file.created_at)}
                        </TableCell>
                        <TableCell>
                          <a
                            href={filesAPI.getDownloadUrl(file.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchPage;
